import { APPLICANT_ATTRIBUTION_BOTH, CENSOR_MASKS, DEFAULT_CENSOR_MASK } from 'cpool-constants';
import type {
    Schema_Applicant,
    SchemaConsorsSubscriber,
    SchemaConsorsSubscription,
    SchemaEuropaceAntrag,
    SchemaEuropaceAntragsteller,
    SchemaEuropaceHaushalt,
    SchemaLoanRequest,
} from 'cpool-schemas';

import type { Schema_LoanRequest_SlaceApplicantContactCreationRequested } from '../events/LoanRequest';

/** Europace `haushalt` may include `vermoegen`; lead `haushalt` does not — A2 filtering applies when present. */
export type HaushaltApplicant2CensorInput = NonNullable<SchemaLoanRequest['haushalt']> & {
    vermoegen?: {
        bausparvertraege?: Array<{ gehoertZuAntragsteller?: string | null; iban?: string | null }>;
        lebensversicherungen?: Array<{ gehoertZuAntragsteller?: string | null; iban?: string | null }>;
    };
};

export const censorCredit = <T extends { iban?: string | null }>(credit: T) => {
    if (!credit.iban) return credit;
    return {
        ...credit,
        iban: CENSOR_MASKS.IBAN,
    };
};

export const censorApplicant2AttributedItem = <
    T extends { gehoertZuAntragsteller?: string | null; iban?: string | null },
>(
    item: T,
): T => {
    if (item.gehoertZuAntragsteller === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2) return censorCredit(item);
    if (item.gehoertZuAntragsteller === APPLICANT_ATTRIBUTION_BOTH.BEIDE)
        return { ...item, gehoertZuAntragsteller: APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1 };
    return item;
};

export const censorCredits = <T extends { iban?: string | null }>(credits: Array<T>) => {
    return credits.map(censorCredit);
};

export const censorLoanRequest = (loanRequest: SchemaLoanRequest) => {
    /* eslint-disable no-param-reassign */
    if (loanRequest.haushalt?.kontoverbindung?.iban) {
        loanRequest.haushalt.kontoverbindung.iban = CENSOR_MASKS.IBAN;
    }
    if (loanRequest.haushalt.verbindlichkeiten.dispositionskredite) {
        loanRequest.haushalt.verbindlichkeiten.dispositionskredite = censorCredits(
            loanRequest.haushalt.verbindlichkeiten.dispositionskredite,
        );
    }
    if (loanRequest.haushalt.verbindlichkeiten.geschaeftskredite) {
        loanRequest.haushalt.verbindlichkeiten.geschaeftskredite = censorCredits(
            loanRequest.haushalt.verbindlichkeiten.geschaeftskredite,
        );
    }
    if (loanRequest.haushalt.verbindlichkeiten.kreditkarten) {
        loanRequest.haushalt.verbindlichkeiten.kreditkarten = censorCredits(
            loanRequest.haushalt.verbindlichkeiten.kreditkarten,
        );
    }
    if (loanRequest.haushalt.verbindlichkeiten.ratenkredite) {
        loanRequest.haushalt.verbindlichkeiten.ratenkredite = censorCredits(
            loanRequest.haushalt.verbindlichkeiten.ratenkredite,
        );
    }
    if (loanRequest.haushalt.verbindlichkeiten.immobiliendarlehen) {
        loanRequest.haushalt.verbindlichkeiten.immobiliendarlehen = censorCredits(
            loanRequest.haushalt.verbindlichkeiten.immobiliendarlehen,
        );
    }
    if (loanRequest.haushalt.verbindlichkeiten.sonstigeVerbindlichkeiten) {
        loanRequest.haushalt.verbindlichkeiten.sonstigeVerbindlichkeiten = censorCredits(
            loanRequest.haushalt.verbindlichkeiten.sonstigeVerbindlichkeiten,
        );
    }
    /* eslint-enable no-param-reassign */

    return loanRequest;
};

const censorApplicant2AttributedArray = <T extends { gehoertZuAntragsteller?: string | null; iban?: string | null }>(
    items: T[],
): T[] =>
    items
        .filter((item) => item.gehoertZuAntragsteller !== APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2)
        .map(censorApplicant2AttributedItem);

/** Mutates. Same A2 row filtering as in {@link censorLoanRequestApplicant2Only}, for a loan-request-shaped haushalt. */
export const censorHaushaltApplicant2Only = (haushalt: NonNullable<SchemaLoanRequest['haushalt']>) => {
    /* eslint-disable no-param-reassign */
    const v = haushalt.verbindlichkeiten;
    if (v) {
        if (v.dispositionskredite) v.dispositionskredite = censorApplicant2AttributedArray(v.dispositionskredite);
        if (v.geschaeftskredite) v.geschaeftskredite = censorApplicant2AttributedArray(v.geschaeftskredite);
        if (v.kreditkarten) v.kreditkarten = censorApplicant2AttributedArray(v.kreditkarten);
        if (v.ratenkredite) v.ratenkredite = censorApplicant2AttributedArray(v.ratenkredite);
        if (v.immobiliendarlehen) v.immobiliendarlehen = censorApplicant2AttributedArray(v.immobiliendarlehen);
        if (v.sonstigeVerbindlichkeiten)
            v.sonstigeVerbindlichkeiten = censorApplicant2AttributedArray(v.sonstigeVerbindlichkeiten);
        if (v.kontokorrentkredite) v.kontokorrentkredite = censorApplicant2AttributedArray(v.kontokorrentkredite);
        if (v.leasings) v.leasings = censorApplicant2AttributedArray(v.leasings);
    }
    const a = haushalt.ausgaben;
    if (a) {
        if (a.mietausgaben) a.mietausgaben = censorApplicant2AttributedArray(a.mietausgaben);
        if (a.privateKrankenversicherungen)
            a.privateKrankenversicherungen = censorApplicant2AttributedArray(a.privateKrankenversicherungen);
        if (a.sonstigeAusgaben) a.sonstigeAusgaben = censorApplicant2AttributedArray(a.sonstigeAusgaben);
        if (a.unterhaltsverpflichtungen)
            a.unterhaltsverpflichtungen = censorApplicant2AttributedArray(a.unterhaltsverpflichtungen);
    }
    const e = haushalt.einnahmen;
    if (e) {
        if (e.ehegattenunterhalt) e.ehegattenunterhalt = censorApplicant2AttributedArray(e.ehegattenunterhalt);
        if (e.einkuenfteAusKapitalvermoegen)
            e.einkuenfteAusKapitalvermoegen = censorApplicant2AttributedArray(e.einkuenfteAusKapitalvermoegen);
        if (e.einkuenfteAusNebentaetigkeit)
            e.einkuenfteAusNebentaetigkeit = censorApplicant2AttributedArray(e.einkuenfteAusNebentaetigkeit);
        if (e.sonstigeEinnahmen) e.sonstigeEinnahmen = censorApplicant2AttributedArray(e.sonstigeEinnahmen);
        if (e.unbefristeteZusatzrenten)
            e.unbefristeteZusatzrenten = censorApplicant2AttributedArray(e.unbefristeteZusatzrenten);
    }
    if (haushalt.immobilien) haushalt.immobilien = censorApplicant2AttributedArray(haushalt.immobilien);
    if (haushalt.kinder) haushalt.kinder = censorApplicant2AttributedArray(haushalt.kinder);
    const { vermoegen } = haushalt as HaushaltApplicant2CensorInput;
    // NOTE: vermoegen is not curretly in use
    if (vermoegen) {
        if (vermoegen.bausparvertraege) {
            vermoegen.bausparvertraege = censorApplicant2AttributedArray(vermoegen.bausparvertraege);
        }
        if (vermoegen.lebensversicherungen) {
            vermoegen.lebensversicherungen = censorApplicant2AttributedArray(vermoegen.lebensversicherungen);
        }
    }
    const { kontoverbindung } = haushalt;
    if (kontoverbindung) {
        if (kontoverbindung.gehoertZuAntragsteller === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2) {
            haushalt.kontoverbindung = null;
        } else if (kontoverbindung.gehoertZuAntragsteller === APPLICANT_ATTRIBUTION_BOTH.BEIDE) {
            kontoverbindung.gehoertZuAntragsteller = APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1;
        }
    }
    /* eslint-enable no-param-reassign */

    return haushalt;
};

export const censorLoanRequestApplicant2Only = (loanRequest: SchemaLoanRequest) => {
    /* eslint-disable no-param-reassign */
    if (loanRequest.haushalt) {
        loanRequest.haushalt = censorHaushaltApplicant2Only(loanRequest.haushalt);
    }
    /* eslint-enable no-param-reassign */

    return loanRequest;
};

/** Europace vorgang haushalt is not the same TypeScript type as loan-request `haushalt`, but A2 row logic applies to the same nested paths at runtime. */
export const censorEuropaceHaushaltApplicant2Only = (haushalt: SchemaEuropaceHaushalt): SchemaEuropaceHaushalt => {
    return censorHaushaltApplicant2Only(
        haushalt as unknown as NonNullable<SchemaLoanRequest['haushalt']>,
    ) as unknown as SchemaEuropaceHaushalt;
};

export const censorApplicant = (applicant: Schema_Applicant) => {
    /* eslint-disable no-param-reassign */
    // required fields
    applicant.personendaten.email = CENSOR_MASKS.EMAIL;
    applicant.personendaten.geburtsort = DEFAULT_CENSOR_MASK;
    applicant.personendaten.telefonPrivat = CENSOR_MASKS.TELEPHONE;
    applicant.personendaten.nachname = DEFAULT_CENSOR_MASK;
    applicant.personendaten.vorname = DEFAULT_CENSOR_MASK;

    // optional fields
    if (applicant.personendaten.geburtsname) {
        applicant.personendaten.geburtsname = DEFAULT_CENSOR_MASK;
    }
    if (applicant.wohnsituation.anschrift?.hausnummer) {
        applicant.wohnsituation.anschrift.hausnummer = DEFAULT_CENSOR_MASK;
    }
    if (applicant.wohnsituation.anschrift?.strasse) {
        applicant.wohnsituation.anschrift.strasse = DEFAULT_CENSOR_MASK;
    }
    if (applicant.wohnsituation.vorherigeAnschrift?.hausnummer) {
        applicant.wohnsituation.vorherigeAnschrift.hausnummer = DEFAULT_CENSOR_MASK;
    }
    if (applicant.wohnsituation.vorherigeAnschrift?.strasse) {
        applicant.wohnsituation.vorherigeAnschrift.strasse = DEFAULT_CENSOR_MASK;
    }
    /* eslint-enable no-param-reassign */

    return applicant;
};

export const censorEuropaceApplicant = <T extends SchemaEuropaceAntragsteller>(applicant: T) => {
    /* eslint-disable no-param-reassign */
    // optional fields
    if (applicant.personendaten?.geburtsname) {
        applicant.personendaten.geburtsname = DEFAULT_CENSOR_MASK;
    }
    if (applicant.personendaten?.email) {
        applicant.personendaten.email = CENSOR_MASKS.EMAIL;
    }
    if (applicant.personendaten?.geburtsort) {
        applicant.personendaten.geburtsort = DEFAULT_CENSOR_MASK;
    }
    if (applicant.personendaten?.nachname) {
        applicant.personendaten.nachname = DEFAULT_CENSOR_MASK;
    }
    if (applicant.personendaten?.vorname) {
        applicant.personendaten.vorname = DEFAULT_CENSOR_MASK;
    }
    if (applicant.personendaten?.telefonPrivat) {
        applicant.personendaten.telefonPrivat = CENSOR_MASKS.TELEPHONE;
    }
    if (applicant.personendaten?.telefonGeschaeftlich) {
        applicant.personendaten.telefonGeschaeftlich = CENSOR_MASKS.TELEPHONE;
    }
    if (applicant.wohnsituation?.anschrift?.hausnummer) {
        applicant.wohnsituation.anschrift.hausnummer = DEFAULT_CENSOR_MASK;
    }
    if (applicant.wohnsituation?.anschrift?.strasse) {
        applicant.wohnsituation.anschrift.strasse = DEFAULT_CENSOR_MASK;
    }
    if (applicant.wohnsituation?.voranschrift?.hausnummer) {
        applicant.wohnsituation.voranschrift.hausnummer = DEFAULT_CENSOR_MASK;
    }
    if (applicant.wohnsituation?.voranschrift?.strasse) {
        applicant.wohnsituation.voranschrift.strasse = DEFAULT_CENSOR_MASK;
    }
    /* eslint-enable no-param-reassign */

    return applicant;
};

export const censorEuropaceAntragApplicant2Only = <T extends SchemaEuropaceAntrag>(antrag: T): T => {
    /* eslint-disable no-param-reassign */
    if (antrag.identifikationAntragsteller2?.antragstellername) {
        antrag.identifikationAntragsteller2.antragstellername = DEFAULT_CENSOR_MASK;
    }
    /* eslint-enable no-param-reassign */
    return antrag;
};

export const censorEuropaceHousehold = <T extends SchemaEuropaceHaushalt>(household: T) => {
    /* eslint-disable no-param-reassign */
    if (household.kinder) {
        household.kinder = household.kinder.map((kind) => {
            if (kind.name) {
                return {
                    ...kind,
                    name: DEFAULT_CENSOR_MASK,
                };
            }
            return kind;
        });
    }
    if (household.kontoverbindung?.iban) {
        household.kontoverbindung.iban = CENSOR_MASKS.IBAN;
    }
    if (household.verbindlichkeiten?.sonstigeVerbindlichkeiten) {
        household.verbindlichkeiten.sonstigeVerbindlichkeiten = censorCredits(
            household.verbindlichkeiten.sonstigeVerbindlichkeiten,
        );
    }
    if (household.verbindlichkeiten?.dispositionskredite) {
        household.verbindlichkeiten.dispositionskredite = censorCredits(
            household.verbindlichkeiten.dispositionskredite,
        );
    }
    if (household.verbindlichkeiten?.kreditkarten) {
        household.verbindlichkeiten.kreditkarten = censorCredits(household.verbindlichkeiten.kreditkarten);
    }
    if (household.verbindlichkeiten?.ratenkredite) {
        household.verbindlichkeiten.ratenkredite = censorCredits(household.verbindlichkeiten.ratenkredite);
    }
    /* eslint-enable no-param-reassign */

    return household;
};

export const censorEuropaceAntrag = (antrag: SchemaEuropaceAntrag) => {
    /* eslint-disable no-param-reassign */
    if (antrag.identifikationAntragsteller1?.antragstellername) {
        antrag.identifikationAntragsteller1.antragstellername = DEFAULT_CENSOR_MASK;
    }
    if (antrag.identifikationAntragsteller2?.antragstellername) {
        antrag.identifikationAntragsteller2.antragstellername = DEFAULT_CENSOR_MASK;
    }
    /* eslint-enable no-param-reassign */
    return antrag;
};

export const censorConsorsSubscriber = (subscriber: SchemaConsorsSubscriber) => {
    /* eslint-disable no-param-reassign */
    if (subscriber.contactAddress?.email) {
        subscriber.contactAddress.email = CENSOR_MASKS.EMAIL;
    }
    if (subscriber.contactAddress?.telephoneLandline) {
        subscriber.contactAddress.telephoneLandline = CENSOR_MASKS.TELEPHONE;
    }
    if (subscriber.contactAddress?.telephoneMobile) {
        subscriber.contactAddress.telephoneMobile = CENSOR_MASKS.TELEPHONE;
    }
    if (subscriber.rolePlaying === 'MAIN' && subscriber.contactAddress?.street) {
        subscriber.contactAddress.street = CENSOR_MASKS.CONSORS_STREET;
    }
    if (subscriber.birthName) {
        subscriber.birthName = CENSOR_MASKS.CONSORS_BIRTH_NAME;
    }
    if (subscriber.firstName) {
        subscriber.firstName = CENSOR_MASKS.CONSORS_FIRST_LAST_NAME;
    }
    if (subscriber.lastName) {
        subscriber.lastName = CENSOR_MASKS.CONSORS_FIRST_LAST_NAME;
    }
    if (subscriber.placeOfBirth) {
        subscriber.placeOfBirth = CENSOR_MASKS.CONSORS_PLACE_OF_BIRTH;
    }
    if (subscriber.identity) {
        subscriber.identity = {}; // TODO censor identity after data is added to the loan request
    }
    if (subscriber.previousAddress?.street) {
        subscriber.previousAddress.street = CENSOR_MASKS.CONSORS_STREET;
    }
    /* eslint-enable no-param-reassign */
    return subscriber;
};

export const censorConsorsSubscription = (subscription?: SchemaConsorsSubscription | null) => {
    if (!subscription) return subscription;
    /* eslint-disable no-param-reassign */
    if (subscription.bankAccount?.iban) {
        subscription.bankAccount.iban = CENSOR_MASKS.IBAN;
    }
    subscription.subscribers[0] = {
        ...subscription.subscribers[0],
        ...censorConsorsSubscriber(subscription.subscribers[0]!),
    };
    if (subscription.subscribers[1]) {
        subscription.subscribers[1] = {
            ...subscription.subscribers[1],
            ...censorConsorsSubscriber(subscription.subscribers[1]),
        };
    }
    /* eslint-enable no-param-reassign */
    return subscription;
};

export const censorSlaceApplicant = (
    applicant: Schema_LoanRequest_SlaceApplicantContactCreationRequested['applicant'],
) => {
    /* eslint-disable no-param-reassign */
    // required fields
    applicant.personendaten.email = CENSOR_MASKS.EMAIL;
    applicant.personendaten.geburtsort = DEFAULT_CENSOR_MASK;
    applicant.personendaten.telefonPrivat = CENSOR_MASKS.TELEPHONE;
    applicant.personendaten.nachname = DEFAULT_CENSOR_MASK;
    applicant.personendaten.vorname = DEFAULT_CENSOR_MASK;

    // optional fields
    if (applicant.personendaten.geburtsname) {
        applicant.personendaten.geburtsname = DEFAULT_CENSOR_MASK;
    }
    if (applicant.wohnsituation.anschrift?.hausnummer) {
        applicant.wohnsituation.anschrift.hausnummer = DEFAULT_CENSOR_MASK;
    }
    if (applicant.wohnsituation.anschrift?.strasse) {
        applicant.wohnsituation.anschrift.strasse = DEFAULT_CENSOR_MASK;
    }
    if (applicant.wohnsituation.vorherigeAnschrift?.hausnummer) {
        applicant.wohnsituation.vorherigeAnschrift.hausnummer = DEFAULT_CENSOR_MASK;
    }
    if (applicant.wohnsituation.vorherigeAnschrift?.strasse) {
        applicant.wohnsituation.vorherigeAnschrift.strasse = DEFAULT_CENSOR_MASK;
    }
    /* eslint-enable no-param-reassign */

    return applicant;
};
